import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Chip,
  Avatar,
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  useMediaQuery,
  useTheme,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Backdrop,
  CircularProgress,
  Switch,
  Fab
} from '@mui/material';
import {
  Edit,
  Delete,
  PhotoCamera,
  ExpandMore,
  ExpandLess,
  Visibility,
  VisibilityOff,
  Add,
  Search,
  FilterList,
  People
} from '@mui/icons-material';
import api from '../api/axiosConfig';
import { getUserAvatarUrl } from '../utils/mediaUrl';

/**
 * Composant MobileUserCard : affiche une carte utilisateur en mobile
 */
const MobileUserCard = ({ user, onEdit, onDelete, expanded, onToggleExpand, t, getRoleLabel }) => (
  <Paper elevation={2} sx={{ mb: 2, overflow: 'hidden' }}>
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={user.id ? getUserAvatarUrl(user.id) : ''}
            alt={`${user.nom} ${user.prenom}`}
            sx={{ width: 50, height: 50 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box 
              sx={{ 
                width: 10, 
                height: 10, 
                borderRadius: '50%', 
                bgcolor: user.is_active ? '#4caf50' : '#f44336'
              }}
            />
            <Box>
              <Typography variant="subtitle1">{`${user.prenom} ${user.nom}`}</Typography>
              <Typography variant="body2" color="textSecondary">
                {getRoleLabel(user.role)}
              </Typography>
            </Box>
          </Box>
        </Box>
        <IconButton onClick={onToggleExpand}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      {expanded && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {`${t('users.email')}: ${user.email}`}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {`${t('users.username')}: ${user.username}`}
          </Typography>

          {user.groupes && user.groupes.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>{t('users.groups')}:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {user.groupes.map((group) => (
                  <Chip
                    key={group.id}
                    label={group.libelle}
                    size="small"
                    sx={{
                      backgroundColor: group.code_couleur,
                      color: '#fff',
                      mb: 0.5
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
            <IconButton onClick={() => onEdit(user)} color="primary" size="small">
              <Edit />
            </IconButton>
            <IconButton onClick={() => onDelete(user.id)} color="error" size="small">
              <Delete />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  </Paper>
);

const Usuarios = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /**
   * Fonction pour obtenir le label traduit d'un rôle
   */
  const getRoleLabel = (role) => {
    const roleMap = {
      'super admin': t('users.roles.superAdmin'),
      'admin pro': t('users.roles.adminPro'),
      'admin cantera': t('users.roles.adminCantera'),
      'entrenador': t('users.roles.entrenador'),
      'pdp': t('users.roles.pdp'),
      'scout interne': t('users.roles.scoutInterne'),
      'scout externe': t('users.roles.scoutExterne'),
      'scout interne cantera': t('users.roles.scoutInterneCantera'),
      'scout externe cantera': t('users.roles.scoutExterneCantera')
    };
    return roleMap[role] || role;
  };

  const ROLES = [
    { key: 'super admin', category: 'admin' },
    { key: 'admin pro', category: 'admin' },
    { key: 'admin cantera', category: 'admin' },
    { key: 'entrenador', category: 'staff' },
    { key: 'pdp', category: 'staff' },
    { key: 'scout interne', category: 'scout' },
    { key: 'scout externe', category: 'scout' },
    { key: 'scout interne cantera', category: 'scout' },
    { key: 'scout externe cantera', category: 'scout' },
  ];

  const getRoleColor = (category, isSelected) => {
    const colors = {
      admin: { bg: '#e3f2fd', border: '#1565c0', text: '#1565c0', selectedBg: '#1565c0' },
      staff: { bg: '#fce4ec', border: '#c62828', text: '#c62828', selectedBg: '#c62828' },
      scout: { bg: '#e8f5e9', border: '#2e7d32', text: '#2e7d32', selectedBg: '#2e7d32' },
    };
    const c = colors[category] || colors.admin;
    return isSelected
      ? { backgroundColor: c.selectedBg, color: '#fff', borderColor: c.selectedBg }
      : { backgroundColor: c.bg, color: c.text, borderColor: c.border };
  };

  // Liste complète des utilisateurs
  const [users, setUsers] = useState([]);
  // Liste filtrée des utilisateurs (après filtres)
  const [filteredUsers, setFilteredUsers] = useState([]);

  // Rôle de l'utilisateur connecté (pour filtrage)
  const [userRole, setUserRole] = useState(null);

  // Filtres supplémentaires : roleFilter & recherche
  const [roleFilter, setRoleFilter] = useState(''); 
  const [searchQuery, setSearchQuery] = useState('');

  // Filtrage actif/inactif
  // false => ne montre que les actifs (par défaut)
  // true  => montre uniquement les inactifs
  const [showInactive, setShowInactive] = useState(false);

  // États pour les Dialog (ajout, édition, suppression)
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);

  // Gestion de l'utilisateur à supprimer ou à éditer
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);

  // Groupes (pour assigner à un utilisateur)
  const [groups, setGroups] = useState([]);

  // Message d'erreur éventuel
  const [errorMessage, setErrorMessage] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Expansion card sur mobile
  const [expandedCards, setExpandedCards] = useState({});

  // Loading indicator
  const [isLoading, setIsLoading] = useState(false);

  // Création d'un nouvel utilisateur
  const [newUser, setNewUser] = useState({
    nom: '',
    prenom: '',
    email: '',
    username: '',
    role: '',
    password: '',
    groupes: [],
    is_active: true,
    can_install_instat_extension: false
  });

  // Affichage du password en clair ou masqué
  const [showPassword, setShowPassword] = useState(false);

  /**
   * Compression de l'image (optionnel, pour limiter la taille)
   */
  const compressImage = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          const MAX_SIZE = 800;
          if (width > height && width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          } else if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/png' }));
          }, 'image/png', 0.7);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  /**
   * Gère la sélection/changement d'avatar (photo)
   */
  const handleCapturePhoto = async (e, isEdit = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const compressedFile = await compressImage(file);
      const reader = new FileReader();
      
      reader.onload = () => {
        if (isEdit) {
          setUserToEdit(prev => ({
            ...prev,
            avatar: reader.result,
            avatarFile: compressedFile
          }));
        } else {
          setNewUser(prev => ({
            ...prev,
            avatar: reader.result,
            avatarFile: compressedFile
          }));
        }
      };
      
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error("Error processing image:", error);
      setErrorMessage(t('users.imageError'));
    }
  };

  /**
   * Prépare l'édition d'un utilisateur
   */
  const handleEditUser = (user) => {
    setUserToEdit({
      ...user,
      avatar: user.id ? getUserAvatarUrl(user.id) : null,
      groupes: user.groupes ? user.groupes.filter(g => g.id != null).map(g => g.id) : [],
      is_active: user.is_active
    });
    setEditUserDialogOpen(true);
  };

  /**
   * Au chargement, on récupère l'utilisateur connecté, et la liste des users
   */
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));

    if (userData) {
      setUserRole(userData.role);
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  /**
   * Récupère la liste des utilisateurs depuis l'API
   */
  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/users', {
        params: { page, limit: 100 }
      });

      // Vérification de la forme des données renvoyées
      const fetchedUsers = Array.isArray(response.data)
        ? response.data
        : response.data.users || [];

      setUsers(fetchedUsers);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Récupère la liste des groupes (pour l'assignation)
   */
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await api.get('/api/groups');
        setGroups(response.data);
      } catch (error) {
        console.error("Error fetching groups:", error);
      }
    };
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Applique tous les filtres (rôle utilisateur connecté, roleFilter, recherche, actif/inactif)
   */
  useEffect(() => {
    const applyFilters = () => {
      let tempUsers = [...users];

      // 1) Filtrage selon le RÔLE de l'utilisateur connecté
      if (userRole === 'super admin') {
        // super admin => voit tout
      } else if (userRole === 'admin pro') {
        // admin pro => voit "admin pro" et scouts pro
        tempUsers = tempUsers.filter(u =>
          u.role === 'admin pro' ||
          u.role === 'scout interne' ||
          u.role === 'scout externe'
        );
      } else if (userRole === 'admin cantera') {
        // admin cantera => voit 'admin cantera', 'entrenador', 'pdp' et scouts cantera
        tempUsers = tempUsers.filter(
          u =>
            u.role === 'admin cantera' ||
            u.role === 'entrenador' ||
            u.role === 'pdp' ||
            u.role === 'scout interne cantera' ||
            u.role === 'scout externe cantera'
        );
      } else {
        // autre => si vous voulez autoriser l'affichage pour un autre rôle, adaptez ici
        // Sinon, on laisse tel quel. 
      }

      // 2) Filtrage par rôle (boutons)
      if (roleFilter) {
        tempUsers = tempUsers.filter(u => u.role === roleFilter);
      }

      // 3) Filtrage par champ de recherche
      if (searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase();
        tempUsers = tempUsers.filter(u =>
          u.email.toLowerCase().includes(query) ||
          u.nom.toLowerCase().includes(query) ||
          u.prenom.toLowerCase().includes(query) ||
          u.username.toLowerCase().includes(query)
        );
      }

      // 4) Filtrage actif/inactif
      if (!showInactive) {
        // false => actifs uniquement
        tempUsers = tempUsers.filter(u => u.is_active === 1);
      } else {
        // true => inactifs uniquement
        tempUsers = tempUsers.filter(u => u.is_active === 0);
      }

      setFilteredUsers(tempUsers);
    };

    applyFilters();
  }, [users, userRole, roleFilter, searchQuery, showInactive]);

  /**
   * Rôles disponibles dans le <Select> (selon le rôle user connecté)
   */
  const getAvailableRoles = () => {
    if (userRole === 'super admin') {
      return ['super admin', 'admin pro', 'admin cantera', 'entrenador', 'pdp', 'scout interne', 'scout externe', 'scout interne cantera', 'scout externe cantera'];
    }
    if (userRole === 'admin cantera') {
      return ['admin cantera', 'entrenador', 'pdp', 'scout interne cantera', 'scout externe cantera'];
    }
    if (userRole === 'admin pro') {
      return ['admin pro', 'scout interne', 'scout externe'];
    }
    // Autres cas : adapter si besoin
    return [];
  };

  /**
   * Ajout d'un nouvel utilisateur
   */
  const handleAddUser = async () => {
    // Contrôle : si role = 'entrenador', doit avoir au moins un groupe
    if (newUser.role === 'entrenador' && newUser.groupes.length === 0) {
      setErrorMessage(t('users.coachGroupError'));
      return;
    }

    // Champs obligatoires
    if (!newUser.nom || !newUser.prenom || !newUser.email || !newUser.username || !newUser.role || !newUser.password) {
      setErrorMessage(t('users.requiredFieldsError'));
      return;
    }

    const formData = new FormData();
    Object.keys(newUser).forEach(key => {
      if (key === 'groupes') {
        formData.append(key, JSON.stringify(newUser[key]));
      } else if (key === 'avatarFile' && newUser[key]) {
        formData.append('avatar', newUser[key]);
      } else if (key !== 'avatar') {
        // On n'ajoute pas la clé 'avatar' car c'est juste un dataURL
        formData.append(key, newUser[key]);
      }
    });
    setIsLoading(true);
    try {
      await api.post('/api/users', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAddUserDialogOpen(false);
      setNewUser({
        nom: '',
        prenom: '',
        email: '',
        username: '',
        role: '',
        password: '',
        groupes: [],
        is_active: true,
        can_install_instat_extension: false
      });
      setErrorMessage('');
      fetchUsers();
    } catch (error) {
      console.error("Error adding user:", error);
      setErrorMessage(t('users.addError'));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Mise à jour d'un utilisateur existant
   */
  const handleUpdateUser = async () => {
    // Contrôle : si role = 'entrenador', doit avoir au moins un groupe
    if (userToEdit.role === 'entrenador' && userToEdit.groupes.length === 0) {
      setErrorMessage(t('users.coachGroupError'));
      return;
    }

    // Champs obligatoires
    if (!userToEdit.nom || !userToEdit.prenom || !userToEdit.email || !userToEdit.username || !userToEdit.role) {
      setErrorMessage(t('users.requiredFieldsError'));
      return;
    }

    const formData = new FormData();
    Object.keys(userToEdit).forEach(key => {
      if (key === 'groupes') {
        formData.append(key, JSON.stringify(userToEdit[key]));
      } else if (key === 'avatarFile' && userToEdit[key]) {
        formData.append('avatar', userToEdit[key]);
      } else if (key !== 'avatar' && key !== 'id') {
        const value = key === 'is_active' ? String(userToEdit[key]) : userToEdit[key];
        formData.append(key, value);
      }
    });

    setIsLoading(true);
    try {
      await api.put(`/api/users/${userToEdit.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEditUserDialogOpen(false);
      setUserToEdit(null);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      setErrorMessage(t('users.updateError'));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Suppression d'un utilisateur
   */
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setIsLoading(true);
    try {
      await api.delete(`/api/users/${userToDelete}`);
      setUsers(users.filter((u) => u.id !== userToDelete));
      setFilteredUsers(filteredUsers.filter((u) => u.id !== userToDelete));
      setDeleteConfirmDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      console.error("Error deleting user:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Permet de déplier/replier une carte utilisateur en mobile
   */
  const toggleCardExpansion = (userId) => {
    setExpandedCards(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 4 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        {t('users.title')}
      </Typography>

      {/* Bouton "Ajouter un utilisateur" en Desktop */}
      {!isMobile && (
        <Button
          variant="contained"
          color="primary"
          onClick={() => setAddUserDialogOpen(true)}
          sx={{ mb: 4 }}
        >
          {t('users.addUser')}
        </Button>
      )}

      {/* Bouton "Ajouter un utilisateur" en Mobile (Floating Action Button) */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
          }}
          onClick={() => setAddUserDialogOpen(true)}
        >
          <Add />
        </Fab>
      )}

      {/* Barre de filtres et d'informations */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        {/* Ligne du haut : compteur + recherche + switch actif/inactif */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            alignItems: isMobile ? 'stretch' : 'center',
            mb: 2
          }}
        >
          <Chip
            icon={<People />}
            label={`${filteredUsers.length} ${t('users.numberOfUsers').toLowerCase()}`}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600, alignSelf: isMobile ? 'flex-start' : 'center' }}
          />

          <TextField
            variant="outlined"
            size="small"
            fullWidth={isMobile}
            placeholder={t('users.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ flex: isMobile ? undefined : 1 }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Switch
              size="small"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <Typography variant="body2" color="text.secondary" noWrap>
              {showInactive
                ? (isMobile ? t('users.viewInactiveUsers') : t('users.inactiveUsers'))
                : (isMobile ? t('users.viewActiveUsers') : t('users.activeUsers'))
              }
            </Typography>
          </Box>
        </Box>

        {/* Filtres par rôle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <FilterList sx={{ color: 'text.secondary', fontSize: 20 }} />
          {ROLES.map(({ key, category }) => {
            const isSelected = roleFilter === key;
            const colorStyle = getRoleColor(category, isSelected);
            return (
              <Chip
                key={key}
                label={getRoleLabel(key)}
                size="small"
                clickable
                onClick={() => setRoleFilter(isSelected ? '' : key)}
                sx={{
                  fontWeight: isSelected ? 600 : 400,
                  fontSize: '0.8rem',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  transition: 'all 0.15s ease',
                  ...colorStyle,
                  '&:hover': {
                    opacity: 0.85,
                    ...colorStyle,
                  },
                }}
              />
            );
          })}
          {roleFilter && (
            <Chip
              label="✕"
              size="small"
              onClick={() => setRoleFilter('')}
              sx={{
                fontWeight: 600,
                backgroundColor: 'grey.200',
                '&:hover': { backgroundColor: 'grey.300' }
              }}
            />
          )}
        </Box>
      </Paper>

      {/* Vue MOBILE */}
      {isMobile ? (
        <Box>
          {filteredUsers.map((user) => (
            <MobileUserCard
              key={user.id}
              user={user}
              onEdit={() => handleEditUser(user)}
              onDelete={(id) => {
                setUserToDelete(id);
                setDeleteConfirmDialogOpen(true);
              }}
              expanded={expandedCards[user.id]}
              onToggleExpand={() => toggleCardExpansion(user.id)}
              t={t}
              getRoleLabel={getRoleLabel}
            />
          ))}
          {filteredUsers.length === 0 && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body1">{t('users.noUsersFound')}</Typography>
            </Box>
          )}
        </Box>
      ) : (
        // Vue DESKTOP
        <TableContainer component={Paper}>
          <Table aria-label={t('users.title')}>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>{t('users.status')}</TableCell>
                <TableCell>{t('users.firstName')}</TableCell>
                <TableCell>{t('users.lastName')}</TableCell>
                <TableCell>{t('users.email')}</TableCell>
                <TableCell>{t('users.password')}</TableCell>
                <TableCell>{t('users.username')}</TableCell>
                <TableCell>{t('users.role')}</TableCell>
                <TableCell>{t('users.groups')}</TableCell>
                <TableCell>{t('users.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      {user.id && (
                        <img
                          src={getUserAvatarUrl(user.id)}
                          alt={`${user.nom} ${user.prenom}`}
                          style={{ width: 40, height: 40, borderRadius: '50%' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box 
                        sx={{ 
                          width: 10, 
                          height: 10, 
                          borderRadius: '50%', 
                          bgcolor: user.is_active ? '#4caf50' : '#f44336'
                        }}
                      />
                    </TableCell>
                    <TableCell>{user.prenom}</TableCell>
                    <TableCell>{user.nom}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>••••••</TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>
                      {getRoleLabel(user.role)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {user.groupes && user.groupes.map((group) => (
                          <Box
                            key={group.id}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 1,
                              py: 0.5,
                              backgroundColor: group.code_couleur,
                              color: '#fff',
                              borderRadius: '16px',
                              fontSize: '0.75rem',
                            }}
                          >
                            {group.libelle}
                          </Box>
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditUser(user)} color="primary">
                        <Edit />
                      </IconButton>
                      <IconButton 
                        onClick={() => {
                          setUserToDelete(user.id);
                          setDeleteConfirmDialogOpen(true);
                        }} 
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    {t('users.noUsersFound')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog : Ajouter un utilisateur */}
      <Dialog 
        open={addUserDialogOpen} 
        onClose={() => setAddUserDialogOpen(false)} 
        fullWidth 
        maxWidth="sm"
      >
        <DialogContent sx={{ position: 'relative', minHeight: '300px' }}>
          {/* Indicateur de chargement (couvre la Dialog) */}
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9999,
              }}
            >
              <CircularProgress color="primary" />
              <Typography variant="h6" sx={{ color: 'white', mt: 2 }}>
                {t('users.processing')}
              </Typography>
            </Box>
          )}

          <Typography variant="h6">{t('users.newUser')}</Typography>
          {errorMessage && (
            <Typography color="error" sx={{ mt: 1, mb: 2 }}>
              {errorMessage}
            </Typography>
          )}

          {/* Avatar / Photo */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
            <IconButton component="label" sx={{ position: 'relative' }}>
              <Avatar
                sx={{ width: 100, height: 100 }}
                src={newUser.avatar}
                alt="Avatar"
              />
              <input
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(e) => handleCapturePhoto(e, false)}
              />
              <PhotoCamera 
                sx={{ 
                  position: 'absolute', 
                  bottom: 0, 
                  right: 0, 
                  backgroundColor: 'white', 
                  borderRadius: '50%', 
                  padding: '4px' 
                }} 
              />
            </IconButton>
          </Box>

          {/* Nom / Prénom / Email / Username */}
          <TextField
            label={t('users.firstName')}
            variant="outlined"
            fullWidth
            sx={{ mt: 2 }}
            value={newUser.prenom}
            onChange={(e) => setNewUser({ ...newUser, prenom: e.target.value })}
          />
          <TextField
            label={t('users.lastName')}
            variant="outlined"
            fullWidth
            sx={{ mt: 2 }}
            value={newUser.nom}
            onChange={(e) => setNewUser({ ...newUser, nom: e.target.value })}
          />
          <TextField
            label={t('users.email')}
            variant="outlined"
            fullWidth
            sx={{ mt: 2 }}
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
          />
          <TextField
            label={t('users.username')}
            variant="outlined"
            fullWidth
            sx={{ mt: 2 }}
            value={newUser.username}
            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
          />

          {/* Mot de passe */}
          <Box sx={{ position: 'relative' }}>
            <TextField
              label={t('users.password')}
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              sx={{ mt: 2 }}
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)'
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </Box>

          {/* Sélection du Rôle */}
          <Select
            value={newUser.role}
            onChange={(e) => {
              const newRole = e.target.value;
              setNewUser({
                ...newUser,
                role: newRole,
                can_install_instat_extension: newRole === 'super admin' ? true : newUser.can_install_instat_extension
              });
            }}
            displayEmpty
            fullWidth
            sx={{ mt: 2 }}
          >
            <MenuItem value="" disabled>{t('users.selectRole')}</MenuItem>
            {getAvailableRoles().map((role) => (
              <MenuItem key={role} value={role}>
                {getRoleLabel(role)}
              </MenuItem>
            ))}
          </Select>

          {/* Sélection des groupes si role = 'entrenador' (ou 'pdp', selon votre logique) */}
          {newUser.role === 'entrenador' && (
            <Autocomplete
              multiple
              fullWidth
              options={groups}
              getOptionLabel={(group) => group?.libelle || ''}
              value={groups.filter(group => newUser.groupes.includes(group.id))}
              onChange={(event, newValue) => {
                setNewUser({
                  ...newUser,
                  groupes: newValue.map(group => group.id)
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('users.selectGroups')}
                  variant="outlined"
                  sx={{ mt: 2 }}
                />
              )}
              renderOption={(props, group) => (
                <Box 
                  component="li" 
                  {...props} 
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Chip
                    label={group.libelle}
                    size="small"
                    style={{
                      backgroundColor: group.code_couleur,
                      color: '#fff',
                      borderRadius: '16px'
                    }}
                  />
                </Box>
              )}
              renderTags={(groupValues, getTagProps) =>
                groupValues.map((group, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={group.id}
                    label={group.libelle}
                    size="small"
                    style={{
                      backgroundColor: group.code_couleur,
                      color: '#fff',
                      borderRadius: '16px',
                      marginRight: 4
                    }}
                  />
                ))
              }
            />
          )}

          {/* Switch is_active */}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Switch
              checked={newUser.is_active}
              onChange={(e) => setNewUser({ ...newUser, is_active: e.target.checked })}
            />
            <Typography>
              {newUser.is_active ? t('users.userActive') : t('users.userInactive')}
            </Typography>
          </Box>

          {/* Switch can_install_instat_extension (super admin only) */}
          {userRole === 'super admin' && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Switch
                checked={newUser.can_install_instat_extension}
                onChange={(e) => setNewUser({ ...newUser, can_install_instat_extension: e.target.checked })}
              />
              <Typography>{t('users.canInstallInstatExtension')}</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setAddUserDialogOpen(false)} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleAddUser} color="primary" variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog : Éditer un utilisateur */}
      <Dialog
        open={editUserDialogOpen}
        onClose={() => setEditUserDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent sx={{ position: 'relative', minHeight: '300px' }}>
          {/* Indicateur de chargement (couvre la Dialog) */}
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9999,
              }}
            >
              <CircularProgress color="primary" />
              <Typography variant="h6" sx={{ color: 'white', mt: 2 }}>
                {t('users.processing')}
              </Typography>
            </Box>
          )}

          <Typography variant="h6">{t('users.editUser')}</Typography>
          {errorMessage && (
            <Typography color="error" sx={{ mt: 1, mb: 2 }}>
              {errorMessage}
            </Typography>
          )}

          {/* Avatar / Photo */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
            <IconButton component="label" sx={{ position: 'relative' }}>
              <Avatar
                sx={{ width: 100, height: 100 }}
                src={userToEdit?.avatar}
                alt="Avatar"
              />
              <input
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(e) => handleCapturePhoto(e, true)}
              />
              <PhotoCamera 
                sx={{ 
                  position: 'absolute', 
                  bottom: 0, 
                  right: 0, 
                  backgroundColor: 'white', 
                  borderRadius: '50%', 
                  padding: '4px' 
                }} 
              />
            </IconButton>
          </Box>

          {/* Nom / Prénom / Email / Username */}
          <TextField
            label={t('users.firstName')}
            variant="outlined"
            fullWidth
            sx={{ mt: 2 }}
            value={userToEdit?.prenom || ''}
            onChange={(e) => setUserToEdit({ ...userToEdit, prenom: e.target.value })}
          />
          <TextField
            label={t('users.lastName')}
            variant="outlined"
            fullWidth
            sx={{ mt: 2 }}
            value={userToEdit?.nom || ''}
            onChange={(e) => setUserToEdit({ ...userToEdit, nom: e.target.value })}
          />
          <TextField
            label={t('users.email')}
            variant="outlined"
            fullWidth
            sx={{ mt: 2 }}
            value={userToEdit?.email || ''}
            onChange={(e) => setUserToEdit({ ...userToEdit, email: e.target.value })}
          />
          <TextField
            label={t('users.username')}
            variant="outlined"
            fullWidth
            sx={{ mt: 2 }}
            value={userToEdit?.username || ''}
            onChange={(e) => setUserToEdit({ ...userToEdit, username: e.target.value })}
          />

          {/* Mot de passe */}
          <Box sx={{ position: 'relative' }}>
            <TextField
              label={t('users.password')}
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              sx={{ mt: 2 }}
              value={userToEdit?.password || ''}
              onChange={(e) => setUserToEdit({ ...userToEdit, password: e.target.value })}
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)'
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </Box>

          {/* Sélection du Rôle */}
          <Select
            value={userToEdit?.role || ''}
            onChange={(e) => {
              const newRole = e.target.value;
              setUserToEdit({
                ...userToEdit,
                role: newRole,
                can_install_instat_extension: newRole === 'super admin' ? true : userToEdit?.can_install_instat_extension
              });
            }}
            displayEmpty
            fullWidth
            sx={{ mt: 2 }}
          >
            <MenuItem value="" disabled>{t('users.selectRole')}</MenuItem>
            {getAvailableRoles().map((role) => (
              <MenuItem key={role} value={role}>
                {getRoleLabel(role)}
              </MenuItem>
            ))}
          </Select>

          {/* Sélection des groupes si role = 'entrenador' (ou 'pdp') */}
          {userToEdit?.role === 'entrenador' && (
            <Autocomplete
              multiple
              fullWidth
              options={groups}
              getOptionLabel={(group) => group?.libelle || ''}
              value={groups.filter(group => userToEdit.groupes.includes(group.id))}
              onChange={(event, newValue) => {
                setUserToEdit({
                  ...userToEdit,
                  groupes: newValue.map(group => group.id)
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('users.selectGroups')}
                  variant="outlined"
                  sx={{ mt: 2 }}
                />
              )}
              renderOption={(props, group) => (
                <Box 
                  component="li" 
                  {...props} 
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Chip
                    label={group.libelle}
                    size="small"
                    style={{
                      backgroundColor: group.code_couleur,
                      color: '#fff',
                      borderRadius: '16px'
                    }}
                  />
                </Box>
              )}
              renderTags={(groupValues, getTagProps) =>
                groupValues.map((group, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={group.id}
                    label={group.libelle}
                    size="small"
                    style={{
                      backgroundColor: group.code_couleur,
                      color: '#fff',
                      borderRadius: '16px',
                      marginRight: 4
                    }}
                  />
                ))
              }
            />
          )}

          {/* Switch is_active */}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Switch
              checked={userToEdit?.is_active || false}
              onChange={(e) => {
                setUserToEdit({ ...userToEdit, is_active: e.target.checked });
              }}
            />
            <Typography>
              {userToEdit?.is_active ? t('users.userActive') : t('users.userInactive')}
            </Typography>
          </Box>

          {/* Switch can_install_instat_extension (super admin only) */}
          {userRole === 'super admin' && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Switch
                checked={!!userToEdit?.can_install_instat_extension}
                onChange={(e) => {
                  setUserToEdit({ ...userToEdit, can_install_instat_extension: e.target.checked });
                }}
              />
              <Typography>{t('users.canInstallInstatExtension')}</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditUserDialogOpen(false)} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleUpdateUser} color="primary" variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteConfirmDialogOpen}
        onClose={() => setDeleteConfirmDialogOpen(false)}
      >
        <DialogContent>
          <Typography variant="h6">{t('users.confirmDelete')}</Typography>
          <Typography>
            {t('users.deleteWarning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialogOpen(false)} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pagination */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
          size={isMobile ? "small" : "medium"}
        />
      </Box>

      {/* Backdrop global pour le chargement (si besoin) */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" component="div" sx={{ mt: 2 }}>
          {t('users.processing')}
        </Typography>
      </Backdrop>
    </Box>
  );
};

export default Usuarios;
